import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { talleresAPI, usuariosAPI, pagosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { Users, UserPlus, Mail, Shield, Loader2, Trash2, Edit, Eye, EyeOff, AlertTriangle, Info, FileText, UserMinus, CreditCard, ShoppingCart } from 'lucide-react';
import MercadoPagoCheckout from '../../components/MercadoPagoCheckout';
import { formatApiError } from '../../utils/apiError';

const TallerUsuarios = () => {
  const { user } = useAuth();
  const [taller, setTaller] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [togglingAccess, setTogglingAccess] = useState(false);
  const [showComprarUsuarios, setShowComprarUsuarios] = useState(false);
  const [cantidadUsuarios, setCantidadUsuarios] = useState(1);
  const [periodoUsuarios, setPeriodoUsuarios] = useState('mensual'); // 'mensual' o 'anual'
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    cargo: 'tecnico_aeronautico',
    tiene_acceso_docs: true
  });

  // Roles disponibles para usuarios de taller
  const rolesDisponibles = [
    { value: 'dueño', label: 'Dueño' },
    { value: 'rt', label: 'RT (Responsable Técnico)' },
    { value: 'inspector', label: 'Inspector' },
    { value: 'tecnico_aeronautico', label: 'Técnico Aeronáutico' },
    { value: 'mecanico_aeronautico', label: 'Mecánico Aeronáutico' },
    { value: 'operario_idoneo', label: 'Operario Idóneo' },
    { value: 'secretaria', label: 'Secretaria/o' },
    { value: 'administrador', label: 'Administrador/Encargado' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      if (user?.taller_id) {
        const tallerRes = await talleresAPI.getOne(user.taller_id);
        setTaller(tallerRes.data);
        
        // Cargar usuarios del taller
        const usuariosRes = await usuariosAPI.getByTaller(user.taller_id);
        setUsuarios(usuariosRes.data || []);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Calcular límites según suscripción
  const getMaxUsuarios = () => {
    if (!taller) return 1;
    const extra = taller.usuarios_extra || 0;
    switch (taller.nivel_suscripcion) {
      case 'premium': return 999; // Ilimitado
      case 'estandar': return 2 + extra;
      default: return 1 + extra; // status_report
    }
  };

  // Contar usuarios CON ACCESO a documentos (esto es lo que limita el plan)
  const getUsuariosConAcceso = () => {
    return usuarios.filter(u => u.tiene_acceso_docs !== false).length;
  };

  // Contar total de usuarios (para mostrar en la lista)
  const getTotalUsuarios = () => {
    return usuarios.length;
  };

  const puedeAgregarUsuario = () => {
    // Puede agregar si hay slots de acceso disponibles
    // Un usuario sin acceso a docs no ocupa slot
    const max = getMaxUsuarios();
    const conAcceso = getUsuariosConAcceso();
    return conAcceso < max;
  };

  const getRoleLabel = (role) => {
    const found = rolesDisponibles.find(r => r.value === role);
    return found ? found.label : role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'dueño': 'bg-amber-500',
      'rt': 'bg-blue-600',
      'inspector': 'bg-indigo-500',
      'tecnico_aeronautico': 'bg-sky-500',
      'mecanico_aeronautico': 'bg-cyan-500',
      'operario_idoneo': 'bg-teal-500',
      'secretaria': 'bg-pink-500',
      'administrador': 'bg-violet-500',
      'taller_owner': 'bg-amber-500',
      'taller_user': 'bg-sky-500',
    };
    return colors[role] || 'bg-gray-500';
  };

  // Obtener el label del cargo para mostrar
  const getCargoDisplay = (u) => {
    const cargoLabel = u.cargo ? rolesDisponibles.find(r => r.value === u.cargo)?.label : null;
    
    // Si es el titular, mostrar "Titular (cargo)" o solo "Titular"
    if (u.role === 'taller_owner') {
      return cargoLabel ? `Titular (${cargoLabel})` : 'Titular';
    }
    
    // Si tiene cargo específico, usar ese
    if (cargoLabel) return cargoLabel;
    
    return 'Usuario';
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      cargo: 'tecnico_aeronautico',
      tiene_acceso_docs: true
    });
    setEditMode(false);
    setSelectedUser(null);
    setShowPassword(false);
  };

  const openNewForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (u) => {
    setFormData({
      email: u.email || '',
      full_name: u.full_name || '',
      password: '',
      cargo: u.cargo || u.role || 'tecnico_aeronautico',
      tiene_acceso_docs: u.tiene_acceso_docs !== false
    });
    setSelectedUser(u);
    setEditMode(true);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.full_name) {
      toast.error('Email y nombre completo son obligatorios');
      return;
    }

    if (!editMode && !formData.password) {
      toast.error('La contraseña es obligatoria para nuevos usuarios');
      return;
    }

    // Verificar límite de usuarios con acceso
    if (formData.tiene_acceso_docs && !editMode && !puedeAgregarUsuario()) {
      toast.error(`Has alcanzado el límite de ${getMaxUsuarios()} usuarios con acceso a documentos`);
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        taller_id: user.taller_id,
        role: 'taller_user' // Rol técnico para el sistema de permisos
      };
      
      if (!data.password) delete data.password;

      if (editMode && selectedUser) {
        await usuariosAPI.update(selectedUser.id, data);
        toast.success('Usuario actualizado correctamente');
      } else {
        await usuariosAPI.create(data);
        toast.success('Usuario creado correctamente');
      }

      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al guardar usuario'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await usuariosAPI.delete(selectedUser.id);
      toast.success('Usuario eliminado');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const confirmDelete = (u) => {
    setSelectedUser(u);
    setShowDeleteConfirm(true);
  };

  // Función para que el titular ceda o recupere su acceso a documentos
  const toggleMiAcceso = async () => {
    const currentUser = usuarios.find(u => u.id === user.id);
    const tieneAcceso = currentUser?.tiene_acceso_docs !== false;
    
    // Si va a ceder acceso, verificar que haya al menos otro usuario con acceso
    if (tieneAcceso) {
      const otrosConAcceso = usuarios.filter(u => u.id !== user.id && u.tiene_acceso_docs !== false);
      if (otrosConAcceso.length === 0) {
        toast.error('Debes tener al menos un usuario con acceso a documentos antes de ceder tu acceso');
        return;
      }
    }
    
    setTogglingAccess(true);
    try {
      await usuariosAPI.update(user.id, { 
        tiene_acceso_docs: !tieneAcceso 
      });
      
      if (tieneAcceso) {
        toast.success('Has cedido tu acceso a documentos. Ahora solo puedes administrar usuarios.');
      } else {
        toast.success('Has recuperado tu acceso a documentos.');
      }
      
      loadData();
    } catch (error) {
      toast.error('Error al cambiar acceso');
    } finally {
      setTogglingAccess(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  // Solo el owner puede gestionar usuarios
  if (user?.role !== 'taller_owner') {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-4xl mx-auto py-8 px-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No tienes permisos para gestionar usuarios.</p>
              <p className="text-sm text-gray-500 mt-2">Solo el titular del taller puede acceder a esta sección.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const maxUsuarios = getMaxUsuarios();
  const usuariosConAcceso = getUsuariosConAcceso();
  const totalUsuarios = getTotalUsuarios();
  const esPremium = taller?.nivel_suscripcion === 'premium';
  const esEstandar = taller?.nivel_suscripcion === 'estandar';
  const esEstandarPro = taller?.nivel_suscripcion === 'estandar_pro';
  const slotsDisponibles = esPremium ? Math.max(0, 10 - usuariosConAcceso) : Math.max(0, maxUsuarios - usuariosConAcceso);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Gestión de Usuarios</h1>
          {puedeAgregarUsuario() && (
            <Button onClick={openNewForm} className="bg-teal-500 hover:bg-teal-600">
              <UserPlus className="mr-2 h-4 w-4" /> Nuevo Usuario
            </Button>
          )}
        </div>

        {/* Info de límites */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Información de tu Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Suscripción</p>
                <Badge className={
                  esPremium ? 'bg-amber-500' : 
                  esEstandar ? 'bg-sky-500' : 'bg-gray-500'
                }>
                  {taller?.nivel_suscripcion?.toUpperCase() || 'N/A'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Usuarios con acceso</p>
                <p className="font-medium text-lg">
                  {usuariosConAcceso} / {esPremium ? '∞' : maxUsuarios}
                </p>
                {totalUsuarios > usuariosConAcceso && (
                  <p className="text-xs text-gray-400">({totalUsuarios} usuarios totales)</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Slots disponibles</p>
                <p className={`font-medium text-lg ${slotsDisponibles > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {esPremium ? 'Ilimitados' : slotsDisponibles}
                </p>
              </div>
            </div>
            
            {!esPremium && slotsDisponibles === 0 && (
              <Alert className="mt-4 border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <span>Has alcanzado el límite de usuarios con acceso. </span>
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-amber-800 font-semibold underline"
                    onClick={() => setShowComprarUsuarios(true)}
                  >
                    Adquirir usuarios adicionales (desde $50 USD/mes)
                  </Button>
                  <span> o quitar el acceso a documentos de algún usuario existente.</span>
                </AlertDescription>
              </Alert>
            )}

            {/* Botón para adquirir usuarios extra */}
            {!esPremium && (
              <div className="mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="w-full border-teal-300 text-teal-700 hover:bg-teal-50"
                  onClick={() => setShowComprarUsuarios(true)}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Adquirir Usuarios Adicionales
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Card especial para el titular - ceder acceso (solo para estándar/status_report) */}
        {!esPremium && (
          <Card className="mb-6 border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-500" />
                Tu Acceso como Titular
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const miUsuario = usuarios.find(u => u.id === user.id);
                const tengoAcceso = miUsuario?.tiene_acceso_docs !== false;
                
                return (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {tengoAcceso ? (
                          <span className="flex items-center gap-2 text-green-600">
                            <FileText className="h-4 w-4" /> Tienes acceso a documentos
                          </span>
                        ) : (
                          <span className="flex items-center gap-2 text-gray-500">
                            <UserMinus className="h-4 w-4" /> Has cedido tu acceso
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {tengoAcceso 
                          ? 'Puedes ceder tu slot a otro usuario (ej: secretaria) y solo administrar el taller.'
                          : 'Recupera tu acceso para ver documentos. Esto ocupará un slot.'
                        }
                      </p>
                    </div>
                    <Button 
                      variant={tengoAcceso ? "outline" : "default"}
                      onClick={toggleMiAcceso}
                      disabled={togglingAccess || (!tengoAcceso && slotsDisponibles === 0)}
                      className={tengoAcceso ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "bg-teal-500 hover:bg-teal-600"}
                    >
                      {togglingAccess ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : tengoAcceso ? (
                        <UserMinus className="h-4 w-4 mr-2" />
                      ) : (
                        <FileText className="h-4 w-4 mr-2" />
                      )}
                      {tengoAcceso ? 'Ceder mi acceso' : 'Recuperar acceso'}
                    </Button>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Lista de usuarios */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" /> Usuarios del Taller ({usuarios.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usuarios.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No hay usuarios adicionales registrados</p>
                <Button onClick={openNewForm} className="bg-teal-500 hover:bg-teal-600">
                  <UserPlus className="mr-2 h-4 w-4" /> Crear primer usuario
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {usuarios.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full ${u.tiene_acceso_docs !== false ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Users className={`h-6 w-6 ${u.tiene_acceso_docs !== false ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{u.full_name || 'Sin nombre'}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Mail className="h-4 w-4" />
                          {u.email}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getRoleBadgeColor(u.cargo || u.role)}>
                            {getCargoDisplay(u)}
                          </Badge>
                          {u.tiene_acceso_docs === false && (
                            <Badge variant="outline" className="text-gray-500">Sin acceso a docs</Badge>
                          )}
                          {u.id === user.id && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">Tú</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {u.id !== user.id && (
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditForm(u)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => confirmDelete(u)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog: Crear/Editar Usuario */}
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  disabled={editMode}
                  required
                />
                {editMode && (
                  <p className="text-xs text-gray-500">El email no se puede modificar</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Nombre completo *</Label>
                <Input
                  id="full_name"
                  placeholder="Juan Carlos Pérez"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Contraseña {editMode ? '(dejar vacío para mantener)' : '*'}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editMode}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cargo en el Taller *</Label>
                <Select 
                  value={formData.cargo} 
                  onValueChange={(value) => setFormData({...formData, cargo: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rolesDisponibles.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Switch para acceso a documentos (solo si hay límite) */}
              {!esPremium && (
                <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                  <div>
                    <Label htmlFor="acceso_docs" className="font-medium">Acceso a documentos</Label>
                    <p className="text-sm text-gray-500">
                      {formData.tiene_acceso_docs 
                        ? 'Este usuario puede ver e imprimir documentos' 
                        : 'Este usuario solo puede acceder al sistema (sin docs)'}
                    </p>
                  </div>
                  <Switch
                    id="acceso_docs"
                    checked={formData.tiene_acceso_docs}
                    onCheckedChange={(checked) => setFormData({...formData, tiene_acceso_docs: checked})}
                    disabled={!formData.tiene_acceso_docs && !puedeAgregarUsuario()}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600" disabled={saving}>
                  {saving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
                  ) : (
                    editMode ? 'Guardar cambios' : 'Crear usuario'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Confirmar eliminación */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>¿Eliminar usuario?</DialogTitle>
              <DialogDescription>
                Esta acción eliminará permanentemente a <strong>{selectedUser?.full_name || selectedUser?.email}</strong> del sistema. Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Adquirir Usuarios Extra */}
        <Dialog open={showComprarUsuarios} onOpenChange={setShowComprarUsuarios}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-teal-600" />
                Adquirir Usuarios Adicionales
              </DialogTitle>
              <DialogDescription>
                Ampliá la capacidad de tu taller con usuarios extra
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Selector de periodo */}
              <div className="space-y-3">
                <Label>Tipo de plan</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPeriodoUsuarios('mensual')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      periodoUsuarios === 'mensual' 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-900">Mensual</div>
                    <div className="text-2xl font-bold text-teal-600">$50 <span className="text-sm font-normal text-slate-500">USD/usuario</span></div>
                    <div className="text-xs text-slate-500 mt-1">Renovación manual cada mes</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPeriodoUsuarios('anual')}
                    className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                      periodoUsuarios === 'anual' 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                      Ahorrá 25%
                    </div>
                    <div className="font-semibold text-slate-900">Anual</div>
                    <div className="text-2xl font-bold text-teal-600">$450 <span className="text-sm font-normal text-slate-500">USD/usuario</span></div>
                    <div className="text-xs text-slate-500 mt-1">Equivale a $37.50/mes</div>
                  </button>
                </div>
              </div>

              {/* Selector de cantidad */}
              <div className="space-y-3">
                <Label>Cantidad de usuarios</Label>
                <div className="flex items-center gap-4">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCantidadUsuarios(Math.max(1, cantidadUsuarios - 1))}
                    disabled={cantidadUsuarios <= 1}
                  >
                    -
                  </Button>
                  <span className="text-3xl font-bold text-teal-600 w-16 text-center">
                    {cantidadUsuarios}
                  </span>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setCantidadUsuarios(Math.min(50, cantidadUsuarios + 1))}
                    disabled={cantidadUsuarios >= 50}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Resumen */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">
                    {cantidadUsuarios} usuario(s) x USD ${periodoUsuarios === 'mensual' ? '50' : '450'} ({periodoUsuarios})
                  </span>
                  <span className="text-2xl font-bold text-teal-600">
                    USD ${cantidadUsuarios * (periodoUsuarios === 'mensual' ? 50 : 450)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {periodoUsuarios === 'mensual' 
                    ? 'Los usuarios se agregarán inmediatamente. Deberás renovar manualmente cada mes.'
                    : 'Los usuarios se agregarán por 12 meses. Deberás renovar manualmente al año.'}
                </p>
              </div>

              {/* Checkout MercadoPago */}
              <MercadoPagoCheckout
                tipo="usuarios"
                titulo={`${cantidadUsuarios} Usuario(s) Adicional(es) - Plan ${periodoUsuarios}`}
                descripcion={`Usuarios extra para tu taller (${periodoUsuarios})`}
                monto={cantidadUsuarios * (periodoUsuarios === 'mensual' ? 50 : 450)}
                moneda="USD"
                createPreference={async () => {
                  const response = await pagosAPI.crearPreferenciaUsuariosExtra({
                    cantidad: cantidadUsuarios,
                    periodo: periodoUsuarios,
                    email: user?.email,
                    taller_id: user?.taller_id
                  });
                  return response.data;
                }}
                buttonText={`Pagar USD $${cantidadUsuarios * (periodoUsuarios === 'mensual' ? 50 : 450)}`}
                onError={(error) => toast.error(error)}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TallerUsuarios;
